import { cn } from '@/lib/cn';
import { isContainer, nodeLabel, type AuthoringDoc } from '@personalizehq/editor';
import type { BlockNode } from '@personalizehq/schemas/ir';
import {
  Box,
  ChevronDown,
  ChevronRight,
  Columns3,
  Image,
  LayoutGrid,
  Minus,
  MousePointerClick,
  Repeat,
  ScrollText,
  SlidersHorizontal,
  Square,
  Type,
  Video,
} from 'lucide-react';
import { useState } from 'react';

// The doc IS a tree, so the layers panel is a render of it — no parallel model, nothing to keep in
// sync. It walks the EDITABLE tree only: `findNode` deliberately does not descend into `fallback`
// or `repeat.template`, and neither does this. Those are separate authoring surfaces (E5), and a
// selection that landed inside one from here would be a silent structural bug.

export const BLOCK_ICON: Record<BlockNode['block'], typeof Box> = {
  stack: Columns3,
  box: Square,
  scroll: ScrollText,
  grid: LayoutGrid,
  text: Type,
  image: Image,
  video: Video,
  button: MousePointerClick,
  spacer: Minus,
  custom: Box,
  progress: SlidersHorizontal,
  repeat: Repeat,
};

interface LayersTreeProps {
  root: BlockNode;
  labels: AuthoringDoc['labels'];
  selection: string | null;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

export function LayersTree({ root, labels, selection, onSelect, onRename }: LayersTreeProps): React.JSX.Element {
  return (
    <div className="py-1 text-[13px]">
      <Row node={root} depth={0} labels={labels} selection={selection} onSelect={onSelect} onRename={onRename} />
    </div>
  );
}

function Row({
  node,
  depth,
  labels,
  selection,
  onSelect,
  onRename,
}: {
  node: BlockNode;
  depth: number;
  labels: AuthoringDoc['labels'];
  selection: string | null;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const Icon = BLOCK_ICON[node.block];
  const children = isContainer(node) ? node.children : [];
  const selected = selection === node.id;
  const label = nodeLabel(node, labels);
  // Names live on the AUTHORING DOC, not the block — `BlockBase` has no `name`, and it is not
  // getting one for a dashboard tree's sake. An unnamed node derives its summary instead.
  const named = Boolean(labels?.[node.id]);

  return (
    <>
      <div
        role="treeitem"
        aria-selected={selected}
        tabIndex={0}
        onClick={() => onSelect(node.id)}
        onDoubleClick={() => setRenaming(true)}
        onKeyDown={(e) => e.key === 'Enter' && onSelect(node.id)}
        className={cn(
          'flex cursor-default items-center gap-1.5 rounded-md py-1 pr-2 transition-colors',
          selected ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:bg-secondary/60'
        )}
        style={{ paddingLeft: 8 + depth * 12 }}
      >
        {children.length > 0 ? (
          <button
            type="button"
            aria-label={open ? 'Collapse' : 'Expand'}
            className="-m-1 p-1"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : (
          <span className="w-3" />
        )}
        <Icon className="size-3.5 shrink-0" />

        {renaming ? (
          <input
            autoFocus
            aria-label={`Rename ${label}`}
            defaultValue={named ? label : ''}
            placeholder={label}
            className="min-w-0 flex-1 rounded border bg-transparent px-1 outline-none focus:border-indigo-500"
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => {
              onRename(node.id, e.target.value);
              setRenaming(false);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setRenaming(false);
            }}
          />
        ) : (
          <span className={cn('truncate', !named && 'text-muted-foreground')}>{label}</span>
        )}
      </div>

      {open
        ? children.map((child) => (
            <Row
              key={child.id}
              node={child}
              depth={depth + 1}
              labels={labels}
              selection={selection}
              onSelect={onSelect}
              onRename={onRename}
            />
          ))
        : null}
    </>
  );
}
